import Icon, { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Carousel, Modal } from 'antd';
import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import EditIcon from '~/assets/svg/icon-edit.svg?react';
import { AD_DETAILS_API_IDS } from '~/helpers/adDetails';
import { getImageUrl } from '~/helpers/general';
import { useGetFeaturedAttributes } from '~/hooks/useGetFeaturedAttributes';

import './PreviewModal.scss';

const EXCLUDED_ATTRIBUTE_KEYS = ['etat', 'etat_precision', 'labo_commentaire', 'shipping_options'];

function PreviewModal({
  show,
  setShow,
  onPublish,
  onSaveDraftAndQuit,
  saving,
  title,
  description,
  price,
  images,
  attributes,
  lastCategoryId,
  allFormComponents,
}) {
  const { data: featuredAttributes = [] } = useGetFeaturedAttributes(lastCategoryId);

  const formatAttributeValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  // Get all attribute components from allFormComponents, excluding special ones
  const allAttributeComponents = Object.values(allFormComponents || {}).filter(
    (comp) => comp.field_type === 'attribute' && !EXCLUDED_ATTRIBUTE_KEYS.includes(comp.key)
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef(null);
  const characteristicsRef = useRef(null);

  const formatPrice = (p) => {
    if (!p) return null;
    const num = parseFloat(p);
    return isNaN(num) ? null : num.toFixed(2).replace('.', ',') + ' €';
  };

  const state = attributes[AD_DETAILS_API_IDS.ATTRIBUTES.ETAT.MAIN_KEY];
  const state_precision = attributes[AD_DETAILS_API_IDS.ATTRIBUTES.ETAT_PRECISION];

  const publishing = saving.is && saving.publish;
  const savingDraft = saving.is && !saving.publish;

  const goToPreviousImage = () => {
    carouselRef.current?.prev();
  };

  const goToNextImage = () => {
    carouselRef.current?.next();
  };

  const scrollToCharacteristics = () => {
    characteristicsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleClose = () => {
    setShow(false);
    setCurrentImageIndex(0);
  };

  return (
    <Modal open={show} onCancel={handleClose} footer={null} closeIcon={null} width='100%' style={{ top: '12px', paddingBottom: '12px' }}>
      <div className='flex flex-col gap-l'>
        <div className='flex justify-between items-center'>
          <h1>Aperçu de votre annonce</h1>
          <Button onClick={handleClose} type='link' className='btn-icon'>
            <CloseOutlined />
          </Button>
        </div>
        <h2>Votre annonce est-elle complète ? Seriez-vous prêt ou prête à l’acheter ?</h2>
        <ul className='pl-l'>
          <li className='body1'>Tous les défauts sont visibles en photo</li>
          <li className='body1'>Les photos sont nettes, centrées</li>
          <li className='body1'>Les caractéristiques sont renseignées</li>
        </ul>
        <Badge
          count='Aperçu de votre annonce'
          color='#0A64A6'
          showZero
          styles={{
            indicator: {
              transform: 'translate(0%, -50%)',
              boxShadow: 'inherit',
              left: 0,
              right: 'inherit',
            },
            root: {
              width: '100%',
            },
          }}
        >
          <div className='overflow-y-scroll p-2xl border-[2px] border-solid border-grey bg-ivory-background flex flex-col gap-xl h-full max-h-[54.5vh]'>
            <div className='flex flex-col gap-l md:grid md:grid-cols-2 md:gap-2xl'>
              {/* Images */}
              <div className='preview-modal-images-container relative rounded-md overflow-hidden'>
                {images.length > 0 ? (
                  <>
                    <Carousel ref={carouselRef} dots={false} draggable touchMove infinite={false} afterChange={setCurrentImageIndex}>
                      {images
                        .filter((img) => !img.uploading)
                        .map((img, index) => (
                          <div key={index} className='preview-modal-slide'>
                            <div className='preview-modal-slide-inner'>
                              <img
                                src={getImageUrl(img?.url, '900')}
                                alt={`${title} - ${index + 1}`}
                                className='max-w-full max-h-full object-contain aspect-square'
                              />
                            </div>
                          </div>
                        ))}
                    </Carousel>
                    {images.length > 1 && (
                      <>
                        <Button
                          type='text'
                          size='large'
                          icon={<ArrowLeftOutlined />}
                          onClick={goToPreviousImage}
                          disabled={currentImageIndex === 0}
                          className='absolute left-s top-1/2 -translate-y-1/2 btn-icon z-10'
                        />
                        <Button
                          type='text'
                          size='large'
                          icon={<ArrowRightOutlined />}
                          onClick={goToNextImage}
                          disabled={currentImageIndex === images.length - 1}
                          className='absolute right-s top-1/2 -translate-y-1/2 btn-icon z-10'
                        />
                        <span className='absolute right-s bottom-s caption1 leading-relaxed px-xs rounded-full bg-[#e6e6e6] flex w-fit z-10'>
                          {currentImageIndex + 1} / {images.length}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <div className='preview-modal-slide-inner'>
                    <p className='body1 text-grey-medium'>Aucune photo</p>
                  </div>
                )}
              </div>
              {/* Product Information */}
              <div className='flex flex-col gap-l'>
                <h1>{title}</h1>
                <div className='flex flex-col gap-s'>
                  <h2>{formatPrice(price)}</h2>
                  <span className='px-xs py-s bg-info-light text-info-dark rounded-sm capitalize-first-letter w-fit'>{state}</span>
                  {state_precision && (
                    <p className='body1'>
                      <span className='text-grey-medium'>Précision sur l’état :</span>
                      {state_precision}
                    </p>
                  )}
                </div>

                <div className='flex flex-col gap-s'>
                  <p className='body2'>Caractéristiques</p>
                  {featuredAttributes.map((attr) => {
                    const value = attributes[attr.key];
                    return (
                      <div key={attr.key} className='flex flex-col gap-xs'>
                        <div className='flex items-center gap-s justify-between body1'>
                          <span className='text-grey-medium'>{attr.name}</span>
                          <span className='text-right'>{value ? formatAttributeValue(value) : <span className='text-error'>Non renseigné</span>}</span>
                        </div>
                        <hr className='robert-divider' />
                      </div>
                    );
                  })}
                  <Button type='link' onClick={scrollToCharacteristics} className='self-start !p-0 !border-none'>
                    <span className='!font-normal !underline'>Plus de détails</span>
                  </Button>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-l md:grid md:gap-2xl md:grid-cols-2'>
              <div ref={characteristicsRef} className='flex flex-col gap-m'>
                <h2>Caractéristiques</h2>
                <div className='flex flex-col gap-s md:grid md:grid-cols-2 md:gap-m'>
                  {allAttributeComponents.map((attr) => {
                    const value = attributes[attr.key];
                    if (!value) return null;
                    return (
                      <div key={attr.key} className='flex flex-col gap-xs'>
                        <div className='flex items-center gap-s justify-between body1'>
                          <span className='text-grey-medium'>{attr.label}</span>
                          <span className='text-right'>{formatAttributeValue(value)}</span>
                        </div>
                        <hr className='robert-divider' />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className='flex flex-col gap-m'>
                <h2>Description</h2>
                <p className='body1'>
                  {description.split('\n').map((line, index) => (
                    <span key={index}>
                      {line}
                      <br />
                    </span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </Badge>

        {/* Action Buttons */}
        <div className='flex justify-end gap-s items-center'>
          <Button onClick={onSaveDraftAndQuit} loading={savingDraft} disabled={saving.is} type='text'>
            Enregistrer le brouillon et quitter
          </Button>
          <Button onClick={handleClose} disabled={saving.is} icon={<Icon component={EditIcon} />}>
            Modifier
          </Button>
          <Button className='btn-success' onClick={onPublish} loading={publishing} disabled={saving.is}>
            Publier
            <ArrowRightOutlined />
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default connect((state) => {
  const { form_fields, all_form_components } = state.products;

  const title = form_fields[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.TITLE] || '';
  const categories = form_fields[AD_DETAILS_API_IDS.CATEGORIES.CATEGORIE] || [];
  const description = form_fields[AD_DETAILS_API_IDS.TITLE_AND_DESCRIPTION.DESCRIPTION] || '';
  const price = form_fields[AD_DETAILS_API_IDS.TECHNICAL_INFO.PRICE];
  const images = form_fields[AD_DETAILS_API_IDS.IMAGES.IMAGES] || [];
  const attributes = form_fields[AD_DETAILS_API_IDS.ATTRIBUTES.MAIN_KEY] || {};
  const lastCategoryId = categories.length > 0 ? categories[categories.length - 1]?.id : null;

  return {
    title,
    description,
    price,
    images,
    attributes,
    lastCategoryId,
    allFormComponents: all_form_components,
  };
})(PreviewModal);
